import React, { ReactElement, useEffect, useMemo } from "react";
import Text from "@components/shared/text";
import MetaTags from "@components/shared/metaTags";
import initialCall from "@helpers/initial-call";
import { translate } from "@helpers/translationObjectFormatter";
import { GQLCategories } from "@gql/types/gql";
import MainContent from "@components/categories/mainContent";
import { GetStaticPropsContext } from "next";
import { useQuery } from "@tanstack/react-query";
import { GQLDomains } from "@gql/types/gql";
import { getKeyWordsForMetaTags } from "@lib/sharedUtils";
import {
    ICategoriesPageProp,
    ICustomDomain,
    TranslationsObject,
} from "@genericTypes/sharedTypes";
import { useSetAtom } from "jotai";
import { activePageAtom } from "@store/generalStore";

const Pg = (): ReactElement => {
    const { data: domainInfo } = useQuery<ICustomDomain, Error>({
        queryKey: ["domain"],
    });
    const { data: translations } = useQuery<TranslationsObject>([
        "translations",
    ]);
    const { data: pageProps } = useQuery<ICategoriesPageProp, Error>({
        queryKey: ["categoriesPageProps"],
    });
    const setActivePage = useSetAtom(activePageAtom);

    const { categories: data, domainOffers } = pageProps ?? {};

    useEffect(() => {
        setActivePage("categories");
    }, []);

    const checkIfParentCategoryHasOffers = (
        categories: GQLCategories[],
    ): boolean => {
        return categories.some((category) => {
            return domainOffers?.find(
                (offer) => offer.categoryId === category.id,
            );
        });
    };

    const parentCategories = useMemo(() => {
        return data
            ? data?.filter(
                  (category) =>
                      category?.parentId === null &&
                      category.categoryChilds?.length &&
                      category.slug !== "legal" &&
                      checkIfParentCategoryHasOffers(
                          category.categoryChilds as GQLCategories[],
                      ),
              )
            : [];
    }, [data]);

    const allCategories = useMemo(() => {
        const categories: { [x: number]: GQLCategories[] } = {};

        // get all sub categories
        if (data) {
            data?.forEach((item) => {
                if (item && item.parentId) {
                    if (categories[item.parentId]) {
                        categories[item.parentId].push(item);
                    } else {
                        categories[item.parentId] = [item];
                    }
                }
            });
        }

        const parentsIds = Object.keys(categories)?.map((id) => +id);

        // get all torts sub category and move it under legal category
        parentsIds.forEach((catId) => {
            const catchCategory = data
                ? data?.find((category) => category.id === catId)
                : null;

            if (
                catchCategory &&
                parentsIds.indexOf(catchCategory.parentId as number) > -1
            ) {
                categories[catchCategory?.parentId as number] = [
                    ...categories[catchCategory?.parentId as number],
                    ...categories[catId],
                ] as GQLCategories[];
                delete categories[catId];
            }
        });

        return categories;
    }, [data]);

    return (
        <>
            <MetaTags
                title={`${translate(
                    translations,
                    "several_categoriesPage_meta_title",
                )} | Several.com`}
                description={translate(
                    translations,
                    "several_categoriesPage_meta_description",
                )}
                keyWords={
                    getKeyWordsForMetaTags(
                        domainInfo?.domain as GQLDomains,
                    ) as string
                }
            />
            <div className="max-w-7xl w-full mx-auto py-12 px-5">
                <div>
                    <Text type="mainTitle" className="text-center  mb-2">
                        {translate(
                            translations,
                            "several_categoriesPage_mainTitle",
                        )}
                    </Text>
                    <Text className="text-base  text-center  mb-12 text-[#58616D]">
                        {translate(
                            translations,
                            "several_categoriesPage_mainDescription",
                        )}
                    </Text>
                    <MainContent
                        parentCategories={parentCategories}
                        allCategories={allCategories}
                    />
                </div>
            </div>
        </>
    );
};

export const getStaticProps = async (ctx: GetStaticPropsContext) => {
    return await initialCall("Categories", ctx);
};

export default Pg;
